import { Suspense, lazy, useEffect, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { DeferredMount } from "./DeferredMount";

// Μη-κρίσιμα widgets: φορτώνονται σε ξεχωριστά chunks μετά το idle, ώστε το JS
// τους να μην αναλύεται/εκτελείται στο κύριο νήμα κατά την αρχική φόρτωση.
const CartSidebar = lazy(() => import("./CartSidebar").then((m) => ({ default: m.CartSidebar })));
const NotificationPrompt = lazy(() =>
  import("./NotificationPrompt").then((m) => ({ default: m.NotificationPrompt })),
);
const InstallPrompt = lazy(() =>
  import("./InstallPrompt").then((m) => ({ default: m.InstallPrompt })),
);
import { AnnouncementBar } from "./AnnouncementBar";
import { useAutoApplyCoupon } from "@/hooks/storefront/use-auto-apply-coupon";
import logo from "@/assets/logo.webp";
import logoWhite from "@/assets/logo-white.webp";

export function StorefrontLayout({
  children,
  hideCartSidebar = false,
  minimalHeader = false,
  minimalFooter = false,
  headerLeft,
  collapsedCart = false,
  mainClassName,
  pinnedCart = false,
}: {
  /** Το πλαϊνό καλάθι μένει πάντα ανοιχτό (π.χ. /pandaki). */
  pinnedCart?: boolean;
  children: ReactNode;
  hideCartSidebar?: boolean;
  minimalHeader?: boolean;
  minimalFooter?: boolean;
  headerLeft?: ReactNode;
  /** Ξεκινά με το πλαϊνό καλάθι μαζεμένο (π.χ. σελίδες μη διαθέσιμων προϊόντων). */
  collapsedCart?: boolean;
  /** Εφαρμόζεται στο <main>, π.χ. γιορτινό φόντο σε μία σελίδα. */
  mainClassName?: string;
}) {
  // Κράτα τυχόν κουπόνι από URL (?coupon=CODE) ώστε να εφαρμοστεί στο checkout.
  useAutoApplyCoupon();
  // Σε σελίδες χωρίς το πλαϊνό drag & drop καλάθι (π.χ. cart, checkout),
  // μηδενίζουμε το πλάτος του καλαθιού ώστε το κουμπί scroll-up να κάθεται
  // κάτω δεξιά της σελίδας στην προβολή υπολογιστών.
  useEffect(() => {
    if (!hideCartSidebar) return;
    const root = document.documentElement;
    root.style.setProperty("--cart-w", "0px");
    return () => {
      root.style.removeProperty("--cart-w");
    };
  }, [hideCartSidebar]);

  return (
    <div
      className={`xmas-bg flex min-h-screen flex-col bg-background ${pinnedCart ? "lg:h-dvh lg:overflow-hidden" : ""} ${
        hideCartSidebar ? "" : "storefront-with-cart"
      }`}
    >
      <AnnouncementBar />
      {minimalHeader ? (
        <header className="sticky top-0 z-40 border-b border-border bg-background">
          <div className="relative mx-auto flex max-w-none items-center justify-center px-4 py-3">
            {headerLeft && (
              <div className="absolute left-4 top-1/2 -translate-y-1/2">{headerLeft}</div>
            )}
            <a
              href="/"
              onClick={(e) => {
                if (window.location.pathname === "/") {
                  e.preventDefault();
                  window.location.reload();
                }
              }}
              className="flex items-center gap-1.5"
              aria-label="MrPanda"
            >
              <img src={logo} alt="MrPanda" width={365} height={96} className="h-10 w-[152px] object-contain object-left sm:h-12 sm:w-[182px] dark:hidden" />
              <img src={logoWhite} alt="MrPanda" width={365} height={96} className="hidden h-10 w-[152px] object-contain object-left sm:h-12 sm:w-[182px] dark:block" />
            </a>
          </div>
        </header>
      ) : (
        <Header showCart={hideCartSidebar} />
      )}
      <main className={`flex-1${pinnedCart ? " relative z-10 lg:min-h-0" : ""}${mainClassName ? ` ${mainClassName}` : ""}`}>{children}</main>
      {pinnedCart ? null : minimalFooter ? (
        <footer className="border-t border-border px-4 pt-2 pb-[calc(5rem+var(--xmas-drift-space,0px))] text-center text-xs text-muted-foreground sm:pb-[calc(0.5rem+var(--xmas-drift-space,0px))]">
          Φτιαγμένο με 💛 από την παρέα του Mr Panda 🐼
          <span className="mx-1.5 hidden sm:inline">·</span>
          <span className="block sm:inline">© {new Date().getFullYear()} MrPanda.gr — Όλα τα δικαιώματα κατοχυρωμένα.</span>
        </footer>
      ) : (
        <Footer />
      )}
      {pinnedCart && (
        <Suspense fallback={null}>
          <CartSidebar pinned />
        </Suspense>
      )}
      <DeferredMount>
        <Suspense fallback={null}>
          {!hideCartSidebar && !pinnedCart && <CartSidebar forceCollapsed={collapsedCart} />}
          <NotificationPrompt />
          <InstallPrompt />
        </Suspense>
      </DeferredMount>
    </div>
  );
}
